import type { ReactNode } from 'react';
import React from 'react';
import { EthereumWalletConnectors } from '@dynamic-labs/ethereum';
import { MagicEvmWalletConnectors } from '@dynamic-labs/magic';
import type { DynamicContextProps, EvmNetwork, OnAuthSuccess } from '@dynamic-labs/sdk-react-core';
import { DynamicContextProvider, SortWallets } from '@dynamic-labs/sdk-react-core';
import { DynamicWagmiConnector } from '@dynamic-labs/wagmi-connector';
import { captureException } from '@sentry/react';
import { debounce } from 'lodash-es';
import { gql } from '@soundxyz/gql-string';
import { ENVIRONMENT, IS_PRODUCTION_DEPLOYMENT } from '@soundxyz/utils/constants/env';
import { theme } from '@components-shared/src/stitches';
import { authUserFilterQueryKey } from '@context/authHelpers';
import { graphqlClientHeaders } from '@context/headers';
import {
  AuthUserDocument,
  fetchGQL,
  fetchQuery,
  VerifyDynamicTokenDocument,
} from '@graphql/reactQuery';
import { AVAILABLE_CHAIN_IDS, RPC_URLS } from '@utils/constants';
import { ApiDataError } from '@utils/error';
import { etherscanURL } from '@utils/helpers';

import { AnalyticsClient } from '../../lib/analytics';
import { AuthEffects, invokeAuthEffect } from './effects';
import { setAuthToken } from './persistence';

const DYNAMIC_ID = (() => {
  // This should panic builds
  if (!process.env.NEXT_PUBLIC_DYNAMIC_ID) {
    throw new Error('NEXT_PUBLIC_DYNAMIC_ID is not defined');
  }
  return process.env.NEXT_PUBLIC_DYNAMIC_ID;
})();

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation verifyDynamicToken($token: JWT!) {
    verifyDynamicJWT(jwt: $token)
  }
`);

// wagmi connector requires privateCustomerRpcUrls
type EvmNetworkWithRpc = EvmNetwork & { privateCustomerRpcUrls: string[] };

const EVM_NETWORKS = ((): EvmNetworkWithRpc[] => {
  const ETH: EvmNetworkWithRpc = {
    blockExplorerUrls: [etherscanURL(AVAILABLE_CHAIN_IDS.eth)],
    chainId: AVAILABLE_CHAIN_IDS.eth,
    name: `Ethereum`,
    iconUrls: ['https://app.dynamic.xyz/assets/networks/eth.svg'],
    nativeCurrency: {
      decimals: 18,
      name: 'Ether',
      symbol: 'ETH',
    },
    privateCustomerRpcUrls: RPC_URLS.eth,
    networkId: AVAILABLE_CHAIN_IDS.eth,
    rpcUrls: RPC_URLS.eth,
    vanityName: `Ethereum`,
  };

  return [
    ETH,
    {
      blockExplorerUrls: [etherscanURL(AVAILABLE_CHAIN_IDS.optimism)],
      chainId: AVAILABLE_CHAIN_IDS.optimism,
      name: `Optimism`,
      iconUrls: ['https://app.dynamic.xyz/assets/networks/optimism.svg'],
      nativeCurrency: {
        decimals: 18,
        name: 'Ether',
        symbol: 'ETH',
      },
      privateCustomerRpcUrls: RPC_URLS.optimism,
      networkId: AVAILABLE_CHAIN_IDS.optimism,
      rpcUrls: RPC_URLS.optimism,
      vanityName: `Optimism`,
    },
    {
      blockExplorerUrls: [etherscanURL(AVAILABLE_CHAIN_IDS.base)],
      chainId: AVAILABLE_CHAIN_IDS.base,
      name: `Base`,
      iconUrls: ['https://app.dynamic.xyz/assets/networks/base.svg'],
      nativeCurrency: {
        decimals: 18,
        name: 'Ether',
        symbol: 'ETH',
      },
      privateCustomerRpcUrls: RPC_URLS.base,
      networkId: AVAILABLE_CHAIN_IDS.base,
      rpcUrls: RPC_URLS.base,
      vanityName: `Base`,
    },
  ];
})();

const onAuthSuccess: OnAuthSuccess = async ({ authToken, user, handleLogOut }) => {
  try {
    const { data } = await fetchGQL(VerifyDynamicTokenDocument, {
      variables: {
        token: authToken,
      },
    });

    if (!data?.verifyDynamicJWT) {
      throw new ApiDataError("Couldn't verify dynamic token with backend");
    }

    setAuthToken(data.verifyDynamicJWT);

    const {
      data: { authUser },
    } = await fetchQuery(AuthUserDocument, {
      fetchOptions: {
        headers: {
          ...graphqlClientHeaders,
          'auth-token': data.verifyDynamicJWT,
        },
      },
      filterQueryKey: authUserFilterQueryKey({
        authToken,
      }),
    });

    if (!authUser) throw new ApiDataError("Couldn't get authUser");

    AnalyticsClient.identify(authUser.id, {
      username: authUser.username,
      isArtist: authUser.artist != null,
      createdAt: authUser.createdAt,
    });

    // Wait until components that use the authenticated user are re-rendered
    await new Promise(resolve => setTimeout(resolve, 10));

    invokeAuthEffect('loginSuccessEnd', {
      authUser,
    });
  } catch (error) {
    captureException(
      {
        error: new Error('Failed to authenticate with backend', { cause: error }),
      },
      {
        extra: {
          dynamicUserId: user.userId,
          dynamicEnvironmentId: user.environmentId,
          dynamicSessionId: user.sessionId,
        },
      },
    );
    invokeAuthEffect('loginFail');
    await handleLogOut();
  } finally {
    invokeAuthEffect('loginStop');
  }
};

const debouncedOnAuthSuccess = debounce(onAuthSuccess, 300, {
  trailing: true,
});

const settings: DynamicContextProps['settings'] = {
  cssOverrides: `
      .modal__backdrop {
        background: rgba(0, 0, 0, 0.5);
      }
      .dynamic-auth-layout__container, .list-tile, .search__container {
        border-radius: 6px;
      }
      .typography {
        font-family: var(${theme.fonts.title.variable});
      }
    `,
  defaultNumberOfWalletsToShow: 4,
  environmentId: DYNAMIC_ID,
  siweStatement: 'Welcome to Sound! Approve this message to securely log in.',
  walletsFilter: SortWallets(['metamask', 'walletconnect', 'coinbase', 'rainbow']),
  evmNetworks: EVM_NETWORKS,
  walletConnectors: [EthereumWalletConnectors, MagicEvmWalletConnectors],
  eventsCallbacks: {
    onAuthSuccess(data) {
      invokeAuthEffect('loginSuccessStart', data);
      debouncedOnAuthSuccess(data);
    },
    onAuthFlowOpen() {
      invokeAuthEffect('loginStart');
    },
    onAuthFlowClose() {
      function invokeEffect() {
        clearTimeout(invokeEffectTimeout);
        cleanupLoginSuccessStart();
        invokeAuthEffect('loginStop');
      }

      const cleanupLoginSuccessStart = AuthEffects.loginSuccessStart(invokeEffect);

      const invokeEffectTimeout = setTimeout(invokeEffect, 1000);
    },
  },
  localStorageSuffix: IS_PRODUCTION_DEPLOYMENT ? '-v18' : '-' + ENVIRONMENT + '-v15',
  showLockedWalletView: false,
};

export function DynamicAuthProvider({ children }: { children: ReactNode }) {
  return (
    <DynamicContextProvider settings={settings}>
      <DynamicWagmiConnector evmNetworks={EVM_NETWORKS}>{children}</DynamicWagmiConnector>
    </DynamicContextProvider>
  );
}
